import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { reOrderFetchedData } from "../../../../hooks/FetchData";

interface CreateScorePayload {
  Id: number;
  RouteId: number;
  GradeLevel: string;
  Score: number;
  //ActiveStatus: boolean;
}
interface ComponentProps {
  Id: number;
  GradeLevel: string;
  Score: number;
  RouteId: number;
  handleClose: () => void;
}


interface RouteTypeData{
  RouteId: number;
  RouteName: string;
}

const EditScoreContent: React.FC<ComponentProps> = ({
  Id,
  handleClose,
  GradeLevel,
  Score,
  RouteId,
}) => {
 
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const GetAllScoreTypes = async (): Promise<any> => {
    return await apiCall().get(`/SetUp/GetAllScoreTypes`);
  };

  const GetAllRoute = async (): Promise<any> => {
    return await apiCall().get(`Routes/GetAllRoute`);
  }

  const [getAllRouteQuery] = useQueries({queries: [
      {queryKey: ['get-allroutes'], queryFn: GetAllRoute, refetchOnWindowFocus: false, retry :1}
  ]}, );


  

  const createScore = async (payload: FormData) => {
    return await apiCallFormData().put(`/Applicant/UpdateOrgAttainment?id=${Id}`, payload);
  };

  const createScoreMutation = useMutation(["update-org-Attainment"], createScore, {
    retry: 0,
  });

  const createScoreHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {

    const createScorePayload = new FormData()

    createScorePayload.append('Id', String(Id))
    createScorePayload.append('RouteId', values.routeId)
    createScorePayload.append('GradeLevel', values.gradeLevel)
    createScorePayload.append('Score', values.score)

    try {
      await createScoreMutation.mutateAsync(createScorePayload, {
        onSuccess: () => {
          showNotification({
            message: "Score created successful",
            type: "success",
          });
          queryClient.refetchQueries(["get-AllOrganAtt"]);
          handleClose();
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error?.message,
        type: "error",
      });
    }
  };

  const validationSchema = Yup.object().shape({
        routeId: Yup.number().typeError('Only numbers are allowed').required('Route Name is required'),
        gradeLevel:Yup.string().required('Grade Level is required'),
        score: Yup.number().typeError('Only numbers are allowed').required('Score is required'),
  });

  const formik = useFormik<FormikValues>({
    initialValues: {
      routeId: RouteId ? RouteId : '',
      gradeLevel: GradeLevel ? GradeLevel :'',
      score: Score ? Score :'',
  },
    onSubmit: (values, { resetForm }) => {
      createScoreHandler(values, resetForm);
    },
    validationSchema,
  });

  const RouteTypeOption =
    getAllRouteQuery &&
    getAllRouteQuery?.data?.data?.Data?.length > 0 ? (
       reOrderFetchedData(getAllRouteQuery?.data?.data?.Data).map((route : RouteTypeData ) => (
            <option value={route?.RouteId} key = {route?.RouteId} >
                {route?.RouteName}
            </option>
            ))
        ) : (
        <option disabled>
            {getAllRouteQuery?.isLoading ? 'loading....' : getAllRouteQuery?.error ? 'error': ''}
        </option>
    )

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <FormWrapper>
          <Field
            name="routeId"
            type="text"
            as={Input}
            label="Route Name"
            options={RouteTypeOption}
            placeholder="Select"
          />
          <Field
            name="gradeLevel"
            type="text"
            as={Input}
            label="Grade Level"
            placeholder="Grade Level"
          />
          <Field
            name="score"
            type="number"
            as={Input}
            label="Score"
            placeholder="Score"
          />
        </FormWrapper>
        <ButtonWrapper>
          <ButtonContainer>
            <StyledButton text="Cancel" onClick={handleClose} />
            <StyledButton
              disabled={createScoreMutation.isLoading}
              text={createScoreMutation.isLoading ? "Updating..." : "Update"}
              onClick={formik.handleSubmit}
              type="submit"
            />
          </ButtonContainer>
        </ButtonWrapper>
      </form>
    </FormikProvider>
  );
};

export default EditScoreContent;

const FormWrapper = styled("div")`
  margin-inline: 3.5rem;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;
